import { useContext, useMemo } from 'react';

import { CustomDataTable } from '@/components/common/ui/table/CustomDataTable';
import { ToastContext } from '@/components/common/CommonToast';
import { useTranslation } from '@/config/i18n';
import useFetchClient from '@/hooks/useFetchClient';
import usePagination from '@/hooks/usePagination';
import useSearch from '@/hooks/useSearch';
import { normalizeTimeZone, toastErrorMessage } from '@/lib/utils';
import { apiRequest } from '@/services';
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '@/shared/constants/pagination';
import { Pagination } from '@/types/pagination';
import { UserColumns } from '@/pages/coach-management/table/UserColumns';

type Props = {
  searchTrigger: number;
};

const CoachRosterList: React.FC<Props> = ({ searchTrigger }) => {
  const [t] = useTranslation('');
  const { apiService } = apiRequest();
  const { searchParams } = useSearch();
  const { toast } = useContext(ToastContext);
  const { query } = Object.fromEntries(searchParams.entries());

  // The server scopes this to the coaches of the signed-in mentor, so no
  // `role` filter is sent — it would be ignored anyway.
  const params = useMemo(() => {
    return query
      ? undefined
      : { limit: DEFAULT_PER_PAGE, paginate: DEFAULT_PAGE };
  }, [query]);

  const queryCustom = useMemo(() => {
    const timezone = normalizeTimeZone(
      Intl.DateTimeFormat().resolvedOptions().timeZone,
    );
    return query ? `${query}&timezone=${timezone}` : `timezone=${timezone}`;
  }, [query]);

  const { data: coaches, error } = useFetchClient(
    async () => await apiService.users.query(queryCustom, params),
    [query, searchTrigger],
    {
      isLoadingGlobal: true,
      isScrollToTop: true,
    },
  );

  if (error) {
    toast?.current?.show(toastErrorMessage(t('common.toast.fail')));
  }

  const { paginationComputed, setPagination } = usePagination(
    coaches?.pagination,
  );

  // Same columns as the coach management screen, minus the action column —
  // the mentor has no write actions here. Reusing the definition keeps the two
  // screens from drifting apart.
  const columns = useMemo(
    () => UserColumns(t).filter((column) => column.dataField !== 'action'),
    [t],
  );

  function onSortChanged(paginationState: Pagination) {
    setPagination(paginationState);
  }

  function onPaginationChanged(paginationState: Pagination) {
    setPagination({
      ...paginationComputed,
      paginate: paginationState.paginate,
      limit: paginationState.limit,
    });
  }

  return (
    <CustomDataTable
      wrapClassName="mt-[10px]"
      values={coaches?.data ?? []}
      columns={columns}
      pagination={paginationComputed}
      onPaginationChanged={onPaginationChanged}
      onSortChanged={onSortChanged}
    />
  );
};

export default CoachRosterList;
